import type { PreflightReportAsset } from "../types/preflight.types";

export interface ReportSummaryStats {
  /** Assets that were actually scored (status SCORED with a score). */
  creativesAnalyzed: number;
  /** Mean score over scored assets only; null when none were scored. */
  avgScore: number | null;
  /** PASS feature verdicts across scored assets (per-criterion aggregation). */
  passedCount: number;
  /** PARTIAL feature verdicts across scored assets. */
  partialCount: number;
  /** FAIL feature verdicts across scored assets. */
  failedCount: number;
  /** Assets that could not be analyzed (failed/pending) — surfaced separately. */
  couldNotAnalyzeCount: number;
}

function isScored(asset: PreflightReportAsset): boolean {
  return asset.status === "SCORED" && asset.score != null;
}

/**
 * Aggregate report stats over **scored assets only** (KTD9). Not-analyzed assets are
 * excluded from `avgScore` and surface as `couldNotAnalyzeCount`. Pass/fail tallies
 * count PASS/FAIL feature verdicts; the three excluded states (NOT_WEIGHTED /
 * NOT_APPLICABLE / NOT_ANALYZED) and PARTIAL never count toward pass or fail.
 */
export function summarizeReport(
  assets: PreflightReportAsset[],
): ReportSummaryStats {
  const scored = assets.filter(isScored);
  const creativesAnalyzed = scored.length;

  const avgScore =
    creativesAnalyzed > 0
      ? Math.round(
          scored.reduce((sum, a) => sum + (a.score ?? 0), 0) /
            creativesAnalyzed,
        )
      : null;

  let passedCount = 0;
  let partialCount = 0;
  let failedCount = 0;
  for (const asset of scored) {
    for (const feature of asset.features) {
      if (feature.state === "PASS") passedCount += 1;
      else if (feature.state === "PARTIAL") partialCount += 1;
      else if (feature.state === "FAIL") failedCount += 1;
    }
  }

  return {
    creativesAnalyzed,
    avgScore,
    passedCount,
    partialCount,
    failedCount,
    couldNotAnalyzeCount: assets.length - creativesAnalyzed,
  };
}
